/**
 * User-list filter DSL builder — METALARK-983.
 *
 * The global Users page and Customer 360's Users tab share one filter bar, and
 * both send every narrowing decision to the server as an advanced-filter
 * object. All of the translation from "what the admin picked" to "what the
 * query says" lives here, in the UI, so the API filter syntax stays untouched:
 * every clause below is built from operators the compiler already supports
 * ($or / $and / $not / $in / $icontains / $startsWith / $isNull).
 *
 * The auth buckets mirror api/utils/userAuthType.ts deriveUserAuthType() so a
 * row's chips and the filter that finds it agree. One known divergence: the
 * backend calls an account federated when `userLogin !== email`, a
 * column-to-column comparison the DSL cannot express. FEDERATED_LOGIN_PREFIXES
 * covers every login this codebase mints, and the missing-@ test covers
 * opaque provider subjects, but a Cognito-brokered SAML/OIDC provider whose
 * NameID is an email address (userLogin `<ProviderName>_user@corp.com`, no
 * externalSubjectKey) reads as native here and as SSO there.
 */

export const AUTH_METHODS = ['sso', 'password', 'passkey', 'breakglass'];

export const AUTH_LABEL_KEYS = {
    sso: 'customer360_users_auth_sso',
    password: 'customer360_users_auth_password',
    passkey: 'customer360_users_auth_passkey',
    breakglass: 'customer360_users_auth_breakglass'
};

export const USER_STATUSES = ['verified', 'unverified', 'asserted', 'locked', 'no_eula'];

export const STATUS_LABEL_KEYS = {
    verified: 'user_filter_verified',
    unverified: 'user_filter_unverified',
    asserted: 'user_filter_asserted',
    locked: 'user_filter_locked',
    no_eula: 'user_filter_noeula'
};

/**
 * Synthetic login prefixes for identities an identity provider owns. Cognito
 * mints `<ProviderName>_<subject>`; JitProvisioningService mints the
 * JwtBearer_ / TrustedIdp_ forms directly.
 */
export const FEDERATED_LOGIN_PREFIXES = [
    'JwtBearer_',
    'TrustedIdp_',
    'Google_',
    'Microsoft_',
    'AuthProxy_'
];

/** Sortable header id -> user column. */
export const USER_SORT_COLUMNS = {
    email: 'email',
    name: 'email',
    lastLogin: 'lastLoginAt',
    lastLoginAt: 'lastLoginAt'
};

export function resolveSortColumn(field) {
    return USER_SORT_COLUMNS[field] || 'email';
}

export function federatedClause() {
    return {
        $or: [
            { externalSubjectKey: { $isNull: false } },
            ...FEDERATED_LOGIN_PREFIXES.map(prefix => ({ userLogin: { $startsWith: prefix } })),
            { $not: { userLogin: { $icontains: '@' } } }
        ]
    };
}

export function nativeClause() {
    return { $not: federatedClause() };
}

/**
 * One auth bucket. Password covers the pending case (a native account with no
 * credentials on file yet) because deriveUserAuthType reports those as
 * 'password' with authPending — they must not fall into the SSO bucket, which
 * is the METALARK-919 bug.
 */
export function authClause(method, scopeCustomerId = '') {
    switch (method) {
        case 'sso':
            return federatedClause();
        case 'passkey':
            return { $and: [nativeClause(), { hasPasskey: true }] };
        case 'password':
            return {
                $and: [
                    nativeClause(),
                    { $or: [{ hasPassword: true }, { hasPasskey: false }] }
                ]
            };
        case 'breakglass': {
            // Scoped inside the one relation object so the customer and the
            // bypass flag have to come from the same assignment row.
            const assignment = { bypassSsoEnforcement: true };
            if (scopeCustomerId) assignment.customerId = scopeCustomerId;
            return { user_role_assignment: assignment };
        }
        default:
            return null;
    }
}

/**
 * One status bucket, in the same precedence the Status column renders: locked
 * wins, then IdP-asserted, then verified/unverified. EULA is an independent
 * line in that column, so it is an independent clause here.
 */
export function statusClause(status) {
    switch (status) {
        case 'locked':
            return { isLocked: true };
        case 'asserted':
            return { $and: [{ isLocked: false }, { externalSubjectKey: { $isNull: false } }] };
        case 'verified':
            return {
                $and: [
                    { isLocked: false },
                    { externalSubjectKey: { $isNull: true } },
                    { emailVerified: true }
                ]
            };
        case 'unverified':
            return {
                $and: [
                    { isLocked: false },
                    { externalSubjectKey: { $isNull: true } },
                    { emailVerified: false }
                ]
            };
        case 'no_eula':
            return { eulaAccepted: false };
        default:
            return null;
    }
}

export function searchClause(term) {
    const search = (term || '').trim();
    if (!search) return null;
    return {
        $or: [
            { email: { $icontains: search } },
            { givenName: { $icontains: search } },
            { familyName: { $icontains: search } },
            { userUuid: { $icontains: search } }
        ]
    };
}

/**
 * Role, customer and workspace share one relation object on purpose: a single
 * JOIN alias means one assignment row has to satisfy all three, so filtering
 * to (Acme, TENANT_ADMIN) cannot match a user who is a TENANT_ADMIN somewhere
 * else and merely a viewer at Acme.
 */
export function assignmentClause(filters = {}, scopeCustomerId = '') {
    const assignment = {};
    const roles = filters.roles || [];
    if (roles.length === 1) assignment.role = roles[0];
    else if (roles.length > 1) assignment.role = { $in: roles };

    const customerId = scopeCustomerId || filters.customerId || '';
    if (customerId) assignment.customerId = customerId;
    if (filters.sliceId) assignment.sliceId = filters.sliceId;

    return Object.keys(assignment).length ? { user_role_assignment: assignment } : null;
}

/**
 * $sort always carries a unique tiebreaker. lastLoginAt ties (and its many
 * nulls) otherwise leave the ordering unstable between LIMIT/OFFSET pages, so
 * rows bleed across page boundaries and selection desyncs (METALARK-1000).
 */
export function sortClause(filters = {}) {
    const direction = filters.sortDirection === 'desc' ? 'desc' : 'asc';
    const field = resolveSortColumn(filters.sortField);
    const sort = [{ field, direction }];
    if (field !== 'userUuid') sort.push({ field: 'userUuid', direction: 'asc' });
    return sort;
}

/**
 * Compose the whole filter. Each active group becomes one element of a
 * top-level $and rather than a top-level key, so the search $or and the auth
 * $or cannot collide on the same key.
 */
/**
 * The active filter, narrowed further to verified accounts, for the user
 * list's "Verified Users" stat. Wrapping the whole built filter in an $and
 * with the verified clause — rather than pushing 'verified' into the status
 * selection — keeps it a narrowing AND. Adding it to the selection would OR it
 * into the status group, so filtering by Locked would count locked-OR-verified
 * instead of the verified subset of the locked list.
 */
export function verifiedCountFilterFrom(filter = {}) {
    const { $sort, ...rest } = filter || {};
    const clauses = [];
    if (Object.keys(rest).length) clauses.push(rest);
    clauses.push(statusClause('verified'));
    return { $sort, $and: clauses };
}

export function buildVerifiedCountFilter(filters = {}, scopeCustomerId = '') {
    return verifiedCountFilterFrom(buildUserListFilter(filters, scopeCustomerId));
}

export function buildUserListFilter(filters = {}, scopeCustomerId = '') {
    const clauses = [];

    const search = searchClause(filters.search);
    if (search) clauses.push(search);

    const assignment = assignmentClause(filters, scopeCustomerId);
    if (assignment) clauses.push(assignment);

    const auth = (filters.auth || [])
        .map(method => authClause(method, scopeCustomerId))
        .filter(Boolean);
    if (auth.length === 1) clauses.push(auth[0]);
    else if (auth.length > 1) clauses.push({ $or: auth });

    const statuses = (filters.statuses || []).map(statusClause).filter(Boolean);
    if (statuses.length === 1) clauses.push(statuses[0]);
    else if (statuses.length > 1) clauses.push({ $or: statuses });

    const filter = { $sort: sortClause(filters) };
    if (clauses.length === 1) Object.assign(filter, clauses[0]);
    else if (clauses.length > 1) filter.$and = clauses;
    return filter;
}
